import { useCallback, useEffect, useState } from 'react';
import { Check, MessageSquare, MessagesSquare, Star, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../components/Modal';
import { Pagination } from '../components/Pagination';
import {
  reviewService,
  type AnswerRow,
  type ModerationStatus,
  type QuestionRow,
  type ReviewRow,
} from '../services/reviewService';
import { fieldClass, formatDateTime, labelClass } from '../utils/form';
import { rowNumber } from '../utils/rowNumber';

type Tab = 'reviews' | 'questions' | 'answers';

function formatDate(iso: string) {
  return formatDateTime(iso);
}

/** Truncated product name with full text on hover. */
function ProductNameCell({ name }: { name: string }) {
  const label = name?.trim() || '—';
  return (
    <td className="px-4 py-3 max-w-[220px] min-w-[140px]">
      <div className="group relative inline-block max-w-full">
        <p
          className="line-clamp-2 cursor-help break-words font-medium leading-snug text-slate-900"
          title={label}
        >
          {label}
        </p>
        <div
          role="tooltip"
          className="pointer-events-none absolute right-0 top-full z-40 mt-1.5 hidden w-max max-w-[min(20rem,70vw)] rounded-lg bg-slate-900 px-3 py-2 text-right text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block"
        >
          {label}
        </div>
      </div>
    </td>
  );
}

export function ReviewsPage() {
  const [tab, setTab] = useState<Tab>('reviews');
  const [status, setStatus] = useState<ModerationStatus>('pending');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<ReviewRow[]>([]);
  const [questions, setQuestions] = useState<QuestionRow[]>([]);
  const [answers, setAnswers] = useState<AnswerRow[]>([]);

  const [answerOpen, setAnswerOpen] = useState(false);
  const [answering, setAnswering] = useState<QuestionRow | null>(null);
  const [answerText, setAnswerText] = useState('');
  const [saving, setSaving] = useState(false);

  const limit = 15;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'reviews') {
        const res = await reviewService.listReviews({ page, limit, status });
        setReviews(res.data ?? []);
        setTotalPages(Math.max(1, res.totalPages || 1));
      } else if (tab === 'questions') {
        const res = await reviewService.listQuestions({ page, limit, status });
        setQuestions(res.data ?? []);
        setTotalPages(Math.max(1, res.totalPages || 1));
      } else {
        const res = await reviewService.listAnswers({ page, limit, status });
        setAnswers(res.data ?? []);
        setTotalPages(Math.max(1, res.totalPages || 1));
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت اطلاعات');
      setReviews([]);
      setQuestions([]);
      setAnswers([]);
    } finally {
      setLoading(false);
    }
  }, [tab, page, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const switchTab = (next: Tab) => {
    setTab(next);
    setPage(1);
  };

  const switchStatus = (next: ModerationStatus) => {
    setStatus(next);
    setPage(1);
  };

  const approveReview = async (id: string) => {
    try {
      await reviewService.approveReview(id);
      toast.success('نظر تأیید شد و روی سایت نمایش داده می‌شود');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در تأیید');
    }
  };

  const rejectReview = async (id: string) => {
    if (!confirm('این نظر حذف شود؟')) return;
    try {
      await reviewService.rejectReview(id);
      toast.success('نظر حذف شد');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در حذف');
    }
  };

  const approveQuestion = async (id: string) => {
    try {
      await reviewService.approveQuestion(id);
      toast.success('پرسش تأیید شد و روی سایت نمایش داده می‌شود');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در تأیید');
    }
  };

  const rejectQuestion = async (id: string) => {
    if (!confirm('این پرسش و همه پاسخ‌هایش حذف شود؟')) return;
    try {
      await reviewService.rejectQuestion(id);
      toast.success('پرسش حذف شد');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در حذف');
    }
  };

  const openAnswer = (q: QuestionRow) => {
    setAnswering(q);
    setAnswerText('');
    setAnswerOpen(true);
  };

  const saveAnswer = async () => {
    if (!answering) return;
    const text = answerText.trim();
    if (!text) {
      toast.error('متن پاسخ را وارد کنید');
      return;
    }
    setSaving(true);
    try {
      await reviewService.createAdminAnswer(answering.id, text);
      toast.success('پاسخ ادمین ثبت و منتشر شد');
      setAnswerOpen(false);
      setAnswering(null);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در ثبت پاسخ');
    } finally {
      setSaving(false);
    }
  };

  const approveAnswer = async (id: string) => {
    try {
      await reviewService.approveAnswer(id);
      toast.success('پاسخ تأیید شد و روی سایت نمایش داده می‌شود');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در تأیید');
    }
  };

  const rejectAnswer = async (id: string) => {
    if (!confirm('این پاسخ حذف شود؟')) return;
    try {
      await reviewService.rejectAnswer(id);
      toast.success('پاسخ حذف شد');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در حذف');
    }
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div>
        <h1 className="page-title text-xl font-bold text-slate-900 sm:text-3xl">نظرات و پرسش‌ها</h1>
        <p className="text-slate-600 mt-1">
          پرسش‌ها و پاسخ‌های مشتریان را جداگانه تأیید کنید. پاسخ ادمین فوری منتشر می‌شود.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => switchTab('reviews')}
          className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium ${
            tab === 'reviews' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 border border-slate-200'
          }`}
        >
          <Star className="h-4 w-4" />
          نظرات
        </button>
        <button
          type="button"
          onClick={() => switchTab('questions')}
          className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium ${
            tab === 'questions' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 border border-slate-200'
          }`}
        >
          <MessageSquare className="h-4 w-4" />
          پرسش‌ها
        </button>
        <button
          type="button"
          onClick={() => switchTab('answers')}
          className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium ${
            tab === 'answers' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 border border-slate-200'
          }`}
        >
          <MessagesSquare className="h-4 w-4" />
          پاسخ‌ها
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {(
          [
            { value: 'pending' as const, label: 'در انتظار تأیید' },
            { value: 'approved' as const, label: 'تأییدشده' },
            { value: '' as const, label: 'همه' },
          ]
        ).map((s) => (
          <button
            key={s.label}
            type="button"
            onClick={() => switchStatus(s.value)}
            className={`rounded-full px-3 py-1.5 text-xs font-medium ${
              status === s.value ? 'bg-emerald-600 text-white' : 'bg-white border border-slate-200 text-slate-600'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500">در حال بارگذاری...</div>
        ) : tab === 'reviews' ? (
          reviews.length === 0 ? (
            <div className="p-10 text-center text-slate-500">نظری یافت نشد</div>
          ) : (
            <div className="admin-table-scroll">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-4 py-3 text-right font-medium">#</th>
                    <th className="px-4 py-3 text-right font-medium">محصول</th>
                    <th className="px-4 py-3 text-right font-medium">نویسنده</th>
                    <th className="px-4 py-3 text-right font-medium">امتیاز</th>
                    <th className="px-4 py-3 text-right font-medium">متن</th>
                    <th className="px-4 py-3 text-right font-medium">وضعیت</th>
                    <th className="px-4 py-3 text-right font-medium">تاریخ</th>
                    <th className="px-4 py-3 text-right font-medium">عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.map((r, i) => (
                    <tr key={r.id} className="border-t border-slate-100 align-top">
                      <td className="px-4 py-3 text-slate-400">{rowNumber(page, limit, i)}</td>
                      <ProductNameCell name={r.productName} />
                      <td className="px-4 py-3 min-w-[150px] max-w-[200px]">
                        <div className="flex flex-col gap-1">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="font-medium text-slate-800 break-words">{r.author}</span>
                            {r.isVerifiedBuyer ? (
                              <span className="inline-flex shrink-0 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-700">
                                خریدار محصول
                              </span>
                            ) : null}
                          </div>
                          {r.userPhone && (
                            <div className="text-xs text-slate-400" dir="ltr">
                              {r.userPhone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 tabular-nums">{r.rating}/5</td>
                      <td className="px-4 py-3 max-w-md">
                        {r.title ? <p className="font-medium text-slate-800 mb-1">{r.title}</p> : null}
                        <p className="text-slate-600 whitespace-pre-wrap">{r.body}</p>
                        {(r.purchasedColors?.length || r.purchasedVariants?.length) ? (
                          <p className="mt-1.5 text-xs font-medium text-indigo-700">
                            {[
                              r.purchasedColors?.length
                                ? `رنگ: ${r.purchasedColors.join('، ')}`
                                : null,
                              r.purchasedVariants?.length
                                ? `تنوع: ${r.purchasedVariants.join('، ')}`
                                : null,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block rounded-full px-2.5 py-1 text-xs font-medium ${
                            r.isApproved ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {r.isApproved ? 'تأییدشده' : 'در انتظار'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDate(r.createdAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          {!r.isApproved && (
                            <button
                              type="button"
                              onClick={() => void approveReview(r.id)}
                              className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs text-white"
                            >
                              <Check className="h-3.5 w-3.5" />
                              تأیید
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => void rejectReview(r.id)}
                            className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1.5 text-xs text-red-700"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            حذف
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : tab === 'questions' ? (
          questions.length === 0 ? (
            <div className="p-10 text-center text-slate-500">پرسشی یافت نشد</div>
          ) : (
            <div className="admin-table-scroll">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-4 py-3 text-right font-medium">#</th>
                    <th className="px-4 py-3 text-right font-medium">محصول</th>
                    <th className="px-4 py-3 text-right font-medium">نویسنده</th>
                    <th className="px-4 py-3 text-right font-medium">پرسش</th>
                    <th className="px-4 py-3 text-right font-medium">پاسخ‌ها</th>
                    <th className="px-4 py-3 text-right font-medium">وضعیت</th>
                    <th className="px-4 py-3 text-right font-medium">تاریخ</th>
                    <th className="px-4 py-3 text-right font-medium">عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {questions.map((q, i) => (
                    <tr key={q.id} className="border-t border-slate-100 align-top">
                      <td className="px-4 py-3 text-slate-400">{rowNumber(page, limit, i)}</td>
                      <ProductNameCell name={q.productName} />
                      <td className="px-4 py-3 min-w-[150px] max-w-[200px]">
                        <div className="flex flex-col gap-1">
                          <span className="font-medium text-slate-800 break-words">{q.author}</span>
                          {q.userPhone && (
                            <div className="text-xs text-slate-400" dir="ltr">
                              {q.userPhone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="cell-wrap px-4 py-3 text-slate-700">{q.body}</td>
                      <td className="px-4 py-3 text-slate-600">
                        <span className="tabular-nums">{q.answersCount}</span>
                        {q.pendingAnswers > 0 ? (
                          <span className="ms-1.5 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">
                            {q.pendingAnswers} در انتظار
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block rounded-full px-2.5 py-1 text-xs font-medium ${
                            q.isApproved ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {q.isApproved ? 'تأییدشده' : 'در انتظار'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDate(q.createdAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          {!q.isApproved && (
                            <button
                              type="button"
                              onClick={() => void approveQuestion(q.id)}
                              className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs text-white"
                            >
                              <Check className="h-3.5 w-3.5" />
                              تأیید پرسش
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => openAnswer(q)}
                            className="inline-flex items-center gap-1 rounded-lg bg-slate-800 px-2.5 py-1.5 text-xs text-white"
                          >
                            پاسخ ادمین
                          </button>
                          <button
                            type="button"
                            onClick={() => void rejectQuestion(q.id)}
                            className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1.5 text-xs text-red-700"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            حذف
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : answers.length === 0 ? (
          <div className="p-10 text-center text-slate-500">پاسخی یافت نشد</div>
        ) : (
          <div className="admin-table-scroll">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-4 py-3 text-right font-medium">#</th>
                  <th className="px-4 py-3 text-right font-medium">محصول</th>
                  <th className="px-4 py-3 text-right font-medium">پرسش</th>
                  <th className="px-4 py-3 text-right font-medium">پاسخ‌دهنده</th>
                  <th className="px-4 py-3 text-right font-medium">پاسخ</th>
                  <th className="px-4 py-3 text-right font-medium">وضعیت</th>
                  <th className="px-4 py-3 text-right font-medium">تاریخ</th>
                  <th className="px-4 py-3 text-right font-medium">عملیات</th>
                </tr>
              </thead>
              <tbody>
                {answers.map((a, i) => (
                  <tr key={a.id} className="border-t border-slate-100 align-top">
                    <td className="px-4 py-3 text-slate-400">{rowNumber(page, limit, i)}</td>
                    <ProductNameCell name={a.productName} />
                    <td className="cell-wrap px-4 py-3">
                      <p className="mb-1 text-xs text-slate-500">{a.questionAuthor}</p>
                      <p className="line-clamp-4 whitespace-pre-wrap text-slate-700">{a.questionBody}</p>
                    </td>
                    <td className="cell-wrap px-4 py-3 min-w-[140px]">
                      <div className="flex flex-col gap-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="font-medium text-slate-800">{a.author}</span>
                          {a.isAdmin ? (
                            <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold text-white">
                              ادمین
                            </span>
                          ) : null}
                          {a.isAnonymous ? (
                            <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold text-slate-700">
                              ناشناس در سایت
                            </span>
                          ) : null}
                          {!a.isAdmin && a.isVerifiedBuyer ? (
                            <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-700">
                              خریدار محصول
                            </span>
                          ) : null}
                        </div>
                        {a.userPhone && (
                          <div className="text-xs text-slate-400" dir="ltr">
                            {a.userPhone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="cell-wrap px-4 py-3 text-slate-700">{a.body}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block rounded-full px-2.5 py-1 text-xs font-medium ${
                          a.isApproved ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {a.isApproved ? 'تأییدشده' : 'در انتظار'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDate(a.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        {!a.isApproved && (
                          <button
                            type="button"
                            onClick={() => void approveAnswer(a.id)}
                            className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs text-white"
                          >
                            <Check className="h-3.5 w-3.5" />
                            تأیید
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => void rejectAnswer(a.id)}
                          className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1.5 text-xs text-red-700"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          حذف
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />

      <Modal
        isOpen={answerOpen}
        onClose={() => {
          if (!saving) {
            setAnswerOpen(false);
            setAnswering(null);
          }
        }}
        title="پاسخ ادمین"
      >
        {answering && (
          <div className="space-y-4">
            <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700">
              <p className="font-medium text-slate-900 mb-1">{answering.productName}</p>
              <p className="text-xs text-slate-500 mb-2">پرسش {answering.author}:</p>
              <p className="whitespace-pre-wrap">{answering.body}</p>
            </div>
            <div>
              <label className={labelClass}>پاسخ (با تگ ادمین منتشر می‌شود)</label>
              <textarea
                className={fieldClass + ' min-h-[120px]'}
                value={answerText}
                onChange={(e) => setAnswerText(e.target.value)}
                placeholder="پاسخ خود را بنویسید..."
              />
              <p className="mt-1.5 text-xs text-slate-500">
                با ثبت پاسخ، پرسش هم در صورت نیاز تأیید و پاسخ فوری روی سایت نمایش داده می‌شود.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={saving}
                onClick={() => {
                  setAnswerOpen(false);
                  setAnswering(null);
                }}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm"
              >
                انصراف
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => void saveAnswer()}
                className="rounded-xl bg-slate-900 px-4 py-2 text-sm text-white disabled:opacity-60"
              >
                {saving ? 'در حال ذخیره...' : 'ثبت و انتشار پاسخ'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
